import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ITechnician } from '../../../utils/entities/interface';
import {
  capitalizeWords,
  formatDate,
  formatTechnicianType,
} from '../../../utils/helper';
import {
  Edit,
  DomainVerification,
  Close,
  ZoomIn,
  Delete,
  Person,
} from '@mui/icons-material';
import SideDrawer from '../../../components/atoms/Drawer';
import { ManageKYC } from './managekyc';
import {
  TechnicianKycStatus,
  TechnicianPosition,
  TechnicianStatus,
  TechnicianType,
} from '../../../utils/entities/type';
import { DocumentUpload } from '../../../components/ui/add/document';
import { IconPrimaryButton } from '../../../components/atoms/Button';
import { AddEditTechnicianModal } from '../../../components/ui/add/technicianModal';
import {
  AddDocument,
  EditTechnician,
  ToggleTechnicianStatus,
  UpdateKycStatus,
} from '../../../services/technician';
import useSnackbar from '../../../hooks/useSnackbar';
import { RotatableImage } from '../../../components/atoms/RotatableImage';

interface KycDrawer {
  open: boolean;
  disable: boolean;
  title: string;
}

interface AddressType {
  house?: string | number | null;
  street?: string | null;
  landmark?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | number | null;
}

interface TechnicianCardProps {
  technician: ITechnician | null;
  onRefresh: () => void;
  address?: string | AddressType | null;
}

export const TechnicianCard: React.FC<TechnicianCardProps> = ({
  technician,
  onRefresh,
  address,
}) => {
  const navigate = useNavigate();
  const [isEditModal, setIsEditModal] = useState(false);
  const [uploadDocDrawer, setUploadDocDrawer] = useState<boolean>(false);

  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [openDocumentPreview, setOpenDocumentPreview] =
    useState<boolean>(false);
  const [openPhotoPreview, setOpenPhotoPreview] = useState<boolean>(false);
  const [profilePreviewUrl, setProfilePreviewUrl] = useState<string | null>(
    null,
  );
  const [savingProfileRotation, setSavingProfileRotation] =
    useState<boolean>(false);
  const [deletingProfilePhoto, setDeletingProfilePhoto] =
    useState<boolean>(false);
  const [savingDocumentRotation, setSavingDocumentRotation] =
    useState<boolean>(false);

  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const { SnackbarComponent, showSnackbar } = useSnackbar();

  const [isKycDrawerOpen, setKycDrawerOpen] = useState<KycDrawer>({
    open: false,
    disable: false,
    title: '',
  });

  const handleEditModalClose = () => {
    setIsEditModal(false);
  };

  const handleDocumentUpload = (data: any) => {
    console.log('Document Upload Data:', data);
  };

  const handleOpenDocumentPreview = (doc: any) => {
    setSelectedDocument(doc);
    setOpenDocumentPreview(true);
  };

  const handleCloseDocumentPreview = () => {
    setSelectedDocument(null);
    setOpenDocumentPreview(false);
  };

  const handleOpenPhotoPreview = () => {
    setProfilePreviewUrl(technician?.profilePhoto || null);
    setOpenPhotoPreview(true);
  };

  const handleClosePhotoPreview = () => {
    setOpenPhotoPreview(false);
    setProfilePreviewUrl(null);
  };

  const refreshAndClosePreview = () => {
    onRefresh();
    handleCloseDocumentPreview();
  };

  const handleSaveRotatedDocument = async (newUrl: string) => {
    if (!technician?._id || !selectedDocument?.type) return;

    try {
      setSavingDocumentRotation(true);

      const status = await AddDocument({
        technicianId: technician._id,
        type: selectedDocument.type,
        docUrl: newUrl,
        comment: selectedDocument.comment || '',
      });

      if (status === 200) {
        showSnackbar('Document rotation saved successfully.', 'success');
        onRefresh();
        setSelectedDocument({ ...selectedDocument, url: newUrl });
      }
    } catch (error) {
      console.log('Document rotation save error:', error);
      showSnackbar('Failed to save rotated document.', 'error');
    } finally {
      setSavingDocumentRotation(false);
    }
  };

  const getTechnicianEditPayload = (profilePhoto: string) => {
    const fallbackAddress =
      typeof address === 'object' && address !== null ? address : {};

    return {
      technicianId: technician!._id,
      name: technician?.name || '',
      email: technician?.email || '',
      profilePhoto,
      joiningDate: technician?.joiningDate
        ? new Date(technician.joiningDate)
        : (null as unknown as Date),
      type: (technician?.type || 'ACD') as TechnicianType,
      position: (technician?.position || 'TBA') as TechnicianPosition,
      phoneNumber: technician?.phoneNumber || '',
      secondaryContactNumber: technician?.secondaryContactNumber || '',
      dob: technician?.dob
        ? new Date(technician.dob)
        : (null as unknown as Date),
      experience: technician?.experience || '',
      house: String((fallbackAddress as AddressType)?.house || ''),
      street: String((fallbackAddress as AddressType)?.street || ''),
      city: String((fallbackAddress as AddressType)?.city || ''),
      state: String((fallbackAddress as AddressType)?.state || ''),
      zipcode: String((fallbackAddress as AddressType)?.zipcode || ''),
      landmark: String((fallbackAddress as AddressType)?.landmark || ''),
    };
  };

  const handleSaveRotatedProfilePhoto = async (newUrl: string) => {
    if (!technician?._id) return;

    try {
      setSavingProfileRotation(true);

      const status = await EditTechnician(getTechnicianEditPayload(newUrl));

      if (status === 200) {
        showSnackbar('Profile photo rotation saved successfully.', 'success');
        onRefresh();
        setProfilePreviewUrl(newUrl);
      }
    } catch (error) {
      console.log('Profile photo rotation save error:', error);
      showSnackbar('Failed to save rotated profile photo.', 'error');
    } finally {
      setSavingProfileRotation(false);
    }
  };

  const handleDeleteProfilePhoto = async () => {
    if (!technician?._id || !profilePreviewUrl) return;

    try {
      setDeletingProfilePhoto(true);

      const status = await EditTechnician(getTechnicianEditPayload(''));

      if (status === 200) {
        showSnackbar('Profile photo deleted successfully.', 'success');
        onRefresh();
        handleClosePhotoPreview();
      }
    } catch (error) {
      console.log('Profile photo delete error:', error);
      showSnackbar('Failed to delete profile photo.', 'error');
    } finally {
      setDeletingProfilePhoto(false);
    }
  };

  const handleKycChange = async (action: 'APPROVE' | 'REJECT') => {
    if (!technician?._id) return;

    try {
      setActionLoading(true);

      const responseStatus = await UpdateKycStatus(technician._id, action);

      if (responseStatus === 200) {
        showSnackbar('Successfully Updated KYC', 'success');

        setTimeout(() => {
          refreshAndClosePreview();
        }, 700);
      }
    } catch (error) {
      console.log('KYC update error:', error);
      showSnackbar('Failed To Update KYC', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleActiveStatus = async () => {
    if (!technician?._id) return;

    try {
      setActionLoading(true);

      const responseStatus = await ToggleTechnicianStatus(technician._id);

      if (responseStatus === 200) {
        showSnackbar('Successfully Updated Technician Status', 'success');

        setTimeout(() => {
          refreshAndClosePreview();
        }, 700);
      }
    } catch (error) {
      console.log('Toggle technician status error:', error);
      showSnackbar('Failed To Update Technician Status', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const formatAddressValue = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || String(value).trim() === '') {
      return '';
    }

    return capitalizeWords(String(value)).replace(/_/g, ' ');
  };

  const formattedAddress =
    typeof address === 'string'
      ? address.trim()
        ? capitalizeWords(address).replace(/_/g, ' ')
        : 'N/A'
      : address
        ? [
            address.house,
            address.street,
            address.landmark,
            address.city,
            address.state,
            address.zipcode,
          ]
            .map(formatAddressValue)
            .filter(Boolean)
            .join(', ') || 'N/A'
        : 'N/A';

  const documentCount = technician?.kycDocs?.length || 0;
  const allowedActions = documentCount >= 2;

  const openManageKycDrawer = () => {
    setKycDrawerOpen({
      open: true,
      disable: false,
      title: 'Manage Kyc',
    });
  };

  const renderManageKycButton = () => (
    <Tooltip title="Manage KYC">
      <Button
        size="small"
        variant="contained"
        color="inherit"
        disabled={actionLoading}
        onClick={openManageKycDrawer}
        startIcon={<DomainVerification sx={{ fontSize: 16 }} />}
        sx={{
          textTransform: 'none',
          minWidth: 0,
          px: 1.1,
          py: 0.55,
          whiteSpace: 'nowrap',
          fontSize: '0.8125rem',
          lineHeight: 1.2,
        }}
      >
        KYC
      </Button>
    </Tooltip>
  );

  const renderKycActionButtons = () => {
    if (!allowedActions) {
      return renderManageKycButton();
    }

    if (
      technician?.status === 'PROFILE_CREATED' ||
      technician?.kycStatus === 'REQUESTED'
    ) {
      return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={actionLoading}
            onClick={() => handleKycChange('APPROVE')}
            sx={{ textTransform: 'none' }}
          >
            Approve
          </Button>

          <Button
            size="small"
            variant="contained"
            color="error"
            disabled={actionLoading}
            onClick={() => handleKycChange('REJECT')}
            sx={{ textTransform: 'none' }}
          >
            Reject
          </Button>
        </Stack>
      );
    }

    if (technician?.kycStatus === 'REJECTED') {
      return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={actionLoading}
            onClick={() => handleKycChange('APPROVE')}
            sx={{ textTransform: 'none' }}
          >
            Approve
          </Button>

          {renderManageKycButton()}
        </Stack>
      );
    }

    if (technician?.kycStatus === 'PENDING') {
      return renderManageKycButton();
    }

    if (
      technician?.kycStatus === 'VERIFIED' &&
      technician?.status === 'DISABLED'
    ) {
      return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
          {renderManageKycButton()}

          <Button
            size="small"
            variant="contained"
            color="error"
            disabled={actionLoading}
            onClick={handleToggleActiveStatus}
            sx={{ textTransform: 'none' }}
          >
            Enable
          </Button>
        </Stack>
      );
    }

    if (
      technician?.kycStatus === 'VERIFIED' &&
      technician?.status !== 'DISABLED'
    ) {
      return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
          <Button
            size="small"
            variant="contained"
            color="error"
            disabled={actionLoading}
            onClick={handleToggleActiveStatus}
            sx={{ textTransform: 'none' }}
          >
            Disable
          </Button>

          {renderManageKycButton()}
        </Stack>
      );
    }

    return null;
  };

  return (
    <>
      {SnackbarComponent}

      <Card
        sx={{
          flex: 1,
          backgroundColor: 'Stroke',
          color: 'white',
          border: '1px solid #444',
        }}
      >
        <SideDrawer
          open={uploadDocDrawer}
          onClose={() => setUploadDocDrawer(false)}
          renderComponent={
            <DocumentUpload
              onClose={() => setUploadDocDrawer(false)}
              onSubmit={handleDocumentUpload}
              onReload={onRefresh}
              technicianId={technician?._id as string}
            />
          }
          renderHeader="Upload New Document"
        />

        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              justifyItems: 'center',
              alignItems: 'center',
              paddingLeft: 1,
              marginBottom: 1,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Basic Detail
            </Typography>

            <Box>
              <IconPrimaryButton
                title="Edit"
                icon={<Edit />}
                onClick={() => setIsEditModal(true)}
                size="small"
              />
            </Box>
          </Box>

          <Box flex={1} pl={1}>
            <Box
              onClick={
                technician?.profilePhoto ? handleOpenPhotoPreview : undefined
              }
              sx={{
                width: 80,
                height: 80,
                borderRadius: 2,
                overflow: 'hidden',
                cursor: technician?.profilePhoto ? 'pointer' : 'default',
                border: '1px solid #333',
                position: 'relative',
                backgroundColor: '#2A2A2A',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover .overlay': {
                  opacity: 1,
                },
              }}
            >
              {technician?.profilePhoto ? (
                <>
                <img
                  src={technician.profilePhoto}
                  alt="Technician Profile"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />

                <Box
                  className="overlay"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.55)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: '0.2s ease',
                  }}
                >
                  <ZoomIn sx={{ color: '#fff', fontSize: 20 }} />
                </Box>
                </>
              ) : (
                <Person sx={{ color: '#8A8D91', fontSize: 42 }} />
              )}
            </Box>
          </Box>

          <Grid container spacing={2} flex={4} pl={1}>
            <Grid item xs={5}>
              <Typography variant="body1" sx={{ color: '#8A8D91' }}>
                Full Name
              </Typography>
            </Grid>

            <Grid item xs={7}>
              <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                {technician?.name || 'N/A'}
              </Typography>
            </Grid>

            <Grid item xs={5}>
              <Typography variant="body1" sx={{ color: '#8A8D91' }}>
                Email Address
              </Typography>
            </Grid>

            <Grid item xs={7}>
              <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                {technician?.email || 'N/A'}
              </Typography>
            </Grid>

            <Grid item xs={5}>
              <Typography variant="body1" sx={{ color: '#8A8D91' }}>
                Contact Number
              </Typography>
            </Grid>

            <Grid item xs={7}>
              <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                {technician?.phoneNumber || 'N/A'}
              </Typography>
            </Grid>

            <Grid item xs={5}>
              <Typography variant="body1" sx={{ color: '#8A8D91' }}>
                Date of Birth
              </Typography>
            </Grid>

            <Grid item xs={7}>
              <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                {(technician?.dob && formatDate(technician.dob)) || 'N/A'}
              </Typography>
            </Grid>

            <Grid item xs={5}>
              <Typography variant="body1" sx={{ color: '#8A8D91' }}>
                Type of Technician
              </Typography>
            </Grid>

            <Grid item xs={7}>
              <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                {formatTechnicianType(technician?.type)}
              </Typography>
            </Grid>

            <Grid item xs={5}>
              <Typography variant="body1" sx={{ color: '#8A8D91' }}>
                Joining Date
              </Typography>
            </Grid>

            <Grid item xs={7}>
              <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                {(technician?.joiningDate &&
                  formatDate(technician.joiningDate)) ||
                  'N/A'}
              </Typography>
            </Grid>

            <Grid item xs={5}>
              <Typography variant="body1" sx={{ color: '#8A8D91' }}>
                Address
              </Typography>
            </Grid>

            <Grid item xs={7}>
              <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                {formattedAddress}
              </Typography>
            </Grid>

            <Grid item xs={5}>
              <Typography variant="body1" sx={{ color: '#8A8D91' }}>
                KYC Status
              </Typography>
            </Grid>

            <Grid item xs={7}>
              <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                {technician?.kycStatus
                  ? capitalizeWords(technician.kycStatus).replace(/_/g, ' ')
                  : 'N/A'}
              </Typography>
            </Grid>

            <Grid item xs={5}>
              <Typography variant="body1" sx={{ color: '#8A8D91' }}>
                Uploaded Documents
              </Typography>
            </Grid>

            <Grid item xs={7}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  minWidth: 0,
                }}
              >
                {technician?.kycDocs?.length ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    {technician.kycDocs.map((doc: any, index: number) => (
                      <Box
                        key={index}
                        onClick={() => handleOpenDocumentPreview(doc)}
                        sx={{
                          position: 'relative',
                          width: 75,
                          height: 50,
                          borderRadius: 1,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                          border: '1px solid #333',
                          backgroundColor: '#111',
                          flexShrink: 0,
                          '&:hover .overlay': {
                            opacity: 1,
                          },
                        }}
                      >
                        <img
                          src={doc.url}
                          alt={`KYC Doc ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />

                        <Box
                          className="overlay"
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            backgroundColor: 'rgba(0,0,0,0.55)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            transition: '0.2s ease',
                          }}
                        >
                          <ZoomIn sx={{ color: '#fff', fontSize: 20 }} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{ color: 'text.secondary', flexShrink: 0 }}
                  >
                    No documents uploaded.
                  </Typography>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  {renderKycActionButtons()}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>

        <SideDrawer
          renderHeader={isKycDrawerOpen.title || 'Closing'}
          size="md"
          renderComponent={
            <ManageKYC
              document={technician?.kycDocs || []}
              kycStatus={technician?.kycStatus as TechnicianKycStatus}
              status={technician?.status as TechnicianStatus}
              disable={isKycDrawerOpen.disable}
              technicianId={technician ? technician._id : ''}
              CompleteProfile={setIsEditModal}
              refreshTechnician={onRefresh}
              onCloseDrawer={() => {
                setKycDrawerOpen({
                  open: false,
                  disable: false,
                  title: '',
                });
              }}
              onChanges={() => {
                setKycDrawerOpen({
                  open: false,
                  disable: false,
                  title: '',
                });
                onRefresh();
              }}
            />
          }
          open={isKycDrawerOpen.open}
          onClose={() => {
            setKycDrawerOpen({
              open: false,
              disable: false,
              title: '',
            });
          }}
        />
      </Card>

      <Dialog
        open={openPhotoPreview}
        onClose={handleClosePhotoPreview}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: '#181818',
            color: '#FFFFFF',
            borderRadius: 3,
            border: '1px solid #333',
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2.5,
            py: 1.5,
            borderBottom: '1px solid #333',
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Profile Photo
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {profilePreviewUrl && (
              <Button
                size="small"
                color="error"
                startIcon={<Delete fontSize="small" />}
                onClick={handleDeleteProfilePhoto}
                disabled={
                  deletingProfilePhoto ||
                  savingProfileRotation ||
                  actionLoading
                }
              >
                Delete
              </Button>
            )}

            <IconButton
              onClick={handleClosePhotoPreview}
              size="small"
              sx={{
                color: '#fff',
                backgroundColor: 'rgba(255,255,255,0.08)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.16)',
                },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ p: 2.5 }}>
          {profilePreviewUrl ? (
            <Box
              sx={{
                borderRadius: 2,
                border: '1px solid #2f2f2f',
                backgroundColor: '#111',
                p: 2,
              }}
            >
              <RotatableImage
                src={profilePreviewUrl}
                alt="Technician Profile Preview"
                folderType={5}
                onRotated={handleSaveRotatedProfilePhoto}
                onSaving={setSavingProfileRotation}
                containerSx={{
                  width: '100%',
                  height: { xs: 260, sm: 340 },
                  overflow: 'hidden',
                  borderRadius: 2,
                }}
                imageStyle={{
                  width: '100%',
                  height: '100%',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  borderRadius: 8,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                py: 6,
                textAlign: 'center',
                borderRadius: 2,
                border: '1px dashed #444',
                backgroundColor: '#111',
              }}
            >
              <Typography sx={{ color: '#999' }}>
                No profile photo uploaded.
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={openDocumentPreview}
        onClose={handleCloseDocumentPreview}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            backgroundColor: '#181818',
            color: '#FFFFFF',
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid #333',
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              height: '82vh',
              backgroundColor: '#0F0F0F',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
              position: 'relative',
            }}
          >
            <IconButton
              onClick={handleCloseDocumentPreview}
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                color: '#fff',
                backgroundColor: 'rgba(0,0,0,0.55)',
                zIndex: 2,
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.75)',
                },
              }}
            >
              <Close />
            </IconButton>

            {selectedDocument?.url ? (
              <RotatableImage
                src={selectedDocument.url}
                alt="Uploaded Document Preview"
                folderType={4}
                onRotated={handleSaveRotatedDocument}
                onSaving={setSavingDocumentRotation}
                containerSx={{
                  width: '100%',
                  height: '100%',
                  minHeight: '70vh',
                }}
                imageStyle={{
                  maxWidth: '100%',
                  maxHeight: '75vh',
                  borderRadius: 10,
                }}
              />
            ) : (
              <Typography sx={{ color: '#999' }}>No image found</Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {isEditModal && (
        <AddEditTechnicianModal
          open={isEditModal}
          onClose={handleEditModalClose}
          onSaved={onRefresh}
          technician={technician}
          action="EDBAS"
        />
      )}
    </>
  );
};
